import { Component, OnInit } from "@angular/core";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { ToastrService } from "ngx-toastr";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import { FlowAnalyticsItem } from "./flow-analytics-chart/flow-analytics-chart.component";

export interface FlowReview {
  rowKey: string;
  flowRowKey: string;
  flowTitle: string;
  flowUniqueId: string;
  isCompleted: boolean;
  hasIssues: boolean;
  reviewText: string;
  mediaUrl: string;
  mediaThumbnailUrl: string;
  isVideo: boolean;
  rating: number;
  createdByUserEmail: string;
  createdByUserName: string;
  createdByFirstName: string;
  createdDate: string;
  profilePic: string;
  creatorResponse: string;
  creatorResponseDate: string;
  helpfulCount?: number;
  moderationStatus?: string;
  issues: FlowReviewIssue[];
}

export interface FlowReviewIssue {
  rowKey: string;
  reviewRowKey: string;
  stepIndex: number;
  stepTitle: string;
  issueDescription: string;
  mediaUrl: string;
  mediaThumbnailUrl: string;
  isVideo: boolean;
  createdDate: string;
  creatorResponse: string;
  creatorResponseDate: string;
  moderationStatus?: string;
}

@Component({
  selector: "app-flow-reviews",
  templateUrl: "./flow-reviews.component.html",
  styleUrls: ["./flow-reviews.component.scss"],
})
export class FlowReviewsComponent implements OnInit {
  userData: any;
  reviews: FlowReview[] = [];
  filteredReviews: FlowReview[] = [];
  isLoading: boolean = false;
  toogleMenuClass: boolean = false;
  searchTerm: string = "";
  flowFilter: string = "All";
  /** All | Pending | Resolved | Completed | Failed — include completed/resolved reviews in the list. */
  statusFilter: string = "All";
  selectedReview: FlowReview | null = null;
  responseText: string = "";
  issueResponseText: Map<string, string> = new Map();
  reviewResponseText: Map<string, string> = new Map();
  userFlows: any[] = [];
  viewMode: 'table' | 'card' = 'table'; // Will be switched to 'card' by default on mobile
  displayedColumns: string[] = ['checkbox', 'user', 'flowName', 'status', 'rating', 'reviewSnippet', 'media'];
  selectedReviews: Set<string> = new Set();
  internalNotes: string = "";
  flowAnalytics: FlowAnalyticsItem[] = [];
  /** Cached list for template (avoids calling getFilteredFlowAnalytics() on every change detection). */
  filteredFlowAnalytics: FlowAnalyticsItem[] = [];
  /** Max analytics cards shown initially; "Show more" reveals rest. Keeps DOM/charts low for large datasets. */
  readonly analyticsDisplayLimit = 12;
  analyticsDisplayCount: number = 12;

  constructor(
    private flowServiceService: FlowServiceService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    private eventEmitterService: EventEmitterService
  ) {}

  ngOnInit(): void {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
    
    if (!this.userData) {
      this.router.navigate(["/registration"]);
      return;
    }

    // Set the correct menu state and page name
    this.eventEmitterService.setDashboardMenuStyleProperty("guide-reviews,Guide Feedback");
    
    // On mobile, default to card view (better UX for narrow screens)
    if (window && window.innerWidth <= 768) {
      this.viewMode = 'card';
    }

    this.loadUserFlows();
  }

  loadUserFlows(): void {
    // Load flows for the filter dropdown (optional - API will get all flows automatically)
    const flowQueryModel = new FlowQueryModel({
      userEmail: this.userData.emailAddress,
      otherUserEmail: this.userData.emailAddress,
      pageIndex: 0,
      category: "All",
    });

    this.flowServiceService.getAllUserFlows(flowQueryModel).subscribe(
      (data) => {
        if (data.flows && data.flows.length > 0) {
          this.userFlows = data.flows;
        }
        // Load reviews - API will automatically get all flow row keys and match with FlowReview table
        this.loadReviews();
      },
      (error) => {
        console.error("Error loading flows for filter:", error);
        // Still load reviews - API will handle getting flow row keys
        this.loadReviews();
      }
    );
  }

  loadReviews(): void {
    this.isLoading = true;
    // API will automatically:
    // 1. Get all flow row keys for the logged-in user from Flow table
    // 2. Match those row keys with FlowReview table (FlowRowKey column)
    // 3. Return all reviews across all flows created by the logged-in user
    // No need to pass FlowRowKeys - API fetches them automatically
    this.flowServiceService
      .getCreatorReviews()
      .subscribe(
        (response: any) => {
          this.reviews = response.reviews || [];
          this.filteredReviews = this.reviews;
          this.buildFlowAnalytics();
          // Update userFlows from response if we didn't get them from loadUserFlows
          if (response.flowRowKeys && response.flowRowKeys.length > 0 && this.userFlows.length === 0) {
            // We have the row keys but not the full flow objects
            // This is fine - the filter dropdown will just be empty
          }
          this.isLoading = false;
        },
        (error) => {
          console.error("Error loading reviews:", error);
          this.toastr.error("Failed to load reviews");
          this.isLoading = false;
        }
      );
  }

  searchReviews(): void {
    this.applyCombinedFilters();
  }

  filterByFlow(): void {
    this.applyCombinedFilters();
  }

  onStatusFilterChange(): void {
    this.applyCombinedFilters();
  }

  /** Pending = not yet Resolved; Resolved = creator marked resolved. */
  isReviewResolved(review: FlowReview): boolean {
    return (review.moderationStatus || "").toLowerCase() === "resolved";
  }

  /** Whether to show the flow filter dropdown below the search input. */
  showFlowDropdown = false;

  /** Close dropdown on blur with a short delay so clicking an option registers. */
  onSearchBlur(): void {
    setTimeout(() => (this.showFlowDropdown = false), 150);
  }

  /** Select a flow filter from the dropdown (or "All") and apply filters. */
  selectFlowFilter(flowRowKey: string, _flowTitle?: string): void {
    this.flowFilter = flowRowKey;
    this.applyCombinedFilters();
    this.showFlowDropdown = false;
  }

  trackByFlowOption(_index: number, flow: { rowKey: string }): string {
    return flow.rowKey;
  }

  /**
   * Flows to show in the dropdown, filtered by search term (flow title).
   * Shown when user focuses the search input; list filters as they type.
   */
  getFilteredUserFlows(): any[] {
    const term = (this.searchTerm || "").trim().toLowerCase();
    if (!term) return this.userFlows;
    return this.userFlows.filter(
      (f) => (f.title || "").toLowerCase().includes(term)
    );
  }

  /**
   * Apply both the text search and the selected flow filter together.
   * - Flow filter narrows down to a specific flow (or all)
   * - Search term then filters within that subset by flow title, user, or review text
   * This keeps the table, cards, and flow analytics all in sync.
   */
  private applyCombinedFilters(): void {
    let result = [...this.reviews];

    // 1) Apply flow dropdown filter (if not "All")
    if (this.flowFilter && this.flowFilter !== "All") {
      result = result.filter((review) => review.flowRowKey === this.flowFilter);
    }

    // 2) Status: Pending / Resolved / Completed (guide finished) / Failed
    if (this.statusFilter && this.statusFilter !== "All") {
      result = result.filter((review) => {
        switch (this.statusFilter) {
          case "Pending":
            return !this.isReviewResolved(review);
          case "Resolved":
            return this.isReviewResolved(review);
          case "Completed":
            return !!review.isCompleted;
          case "Failed":
            return !review.isCompleted;
          default:
            return true;
        }
      });
    }

    // 3) Apply search term across current subset (flows list + reviews)
    const term = this.searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((review) => {
        const flowTitle = review.flowTitle || "";
        const userName =
          review.createdByUserName || review.createdByFirstName || "";
        const reviewText = review.reviewText || "";
        return (
          flowTitle.toLowerCase().includes(term) ||
          userName.toLowerCase().includes(term) ||
          reviewText.toLowerCase().includes(term)
        );
      });
    }

    this.filteredReviews = result;
    this.updateFilteredFlowAnalytics();
  }

  private getWeekKey(dateStr: string): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay());
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, "0");
    const day = String(start.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  buildFlowAnalytics(): void {
    const flowMap = new Map<
      string,
      { flowTitle: string; reviews: FlowReview[] }
    >();
    for (const r of this.reviews) {
      if (!r.flowRowKey) continue;
      if (!flowMap.has(r.flowRowKey)) {
        flowMap.set(r.flowRowKey, { flowTitle: r.flowTitle || "Untitled", reviews: [] });
      }
      flowMap.get(r.flowRowKey)!.reviews.push(r);
    }
    const weeksCount = 6;
    const now = new Date();
    const weekLabels: string[] = [];
    const weekKeys: string[] = [];
    for (let i = weeksCount - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - 7 * i);
      d.setHours(0, 0, 0, 0);
      const key = this.getWeekKey(d.toISOString());
      weekKeys.push(key);
      const weekStart = new Date(d);
      const weekEnd = new Date(d);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const fmt = (x: Date) => x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      weekLabels.push(`${fmt(weekStart)} – ${fmt(weekEnd)}`);
    }
    const timeFrameLabel = "Last 6 weeks";

    this.flowAnalytics = [];
    flowMap.forEach((value, flowRowKey) => {
      const reviews = value.reviews;
      const reviewCount = reviews.length;
      const issueCount = reviews.reduce(
        (sum, r) => sum + (r.issues?.length || 0),
        0
      );
      const reviewsData = weekKeys.map(
        (wk) =>
          reviews.filter((r) => this.getWeekKey(r.createdDate) === wk).length
      );
      const issuesData = weekKeys.map((wk) =>
        reviews
          .filter((r) => this.getWeekKey(r.createdDate) === wk)
          .reduce((s, r) => s + (r.issues?.length || 0), 0)
      );

      // Per-step breakdown: step no, title, issue count and review count on that step
      const stepMap = new Map<
        string,
        { stepIndex: number; stepTitle: string; issueCount: number; reviewIds: Set<string> }
      >();
      reviews.forEach((r) => {
        (r.issues || []).forEach((issue) => {
          const key = `${issue.stepIndex ?? 0}-${(issue.stepTitle || "").trim() || "Step"}`;
          if (!stepMap.has(key)) {
            stepMap.set(key, {
              stepIndex: issue.stepIndex ?? 0,
              stepTitle: issue.stepTitle?.trim() || `Step ${(issue.stepIndex ?? 0) + 1}`,
              issueCount: 0,
              reviewIds: new Set(),
            });
          }
          const entry = stepMap.get(key)!;
          entry.issueCount += 1;
          entry.reviewIds.add(r.rowKey);
        });
      });
      const stepBreakdown = Array.from(stepMap.values())
        .map((e) => ({
          stepIndex: e.stepIndex,
          stepTitle: e.stepTitle,
          issueCount: e.issueCount,
          reviewCount: e.reviewIds.size,
        }))
        .sort((a, b) => a.stepIndex - b.stepIndex);

      this.flowAnalytics.push({
        flowRowKey,
        flowTitle: value.flowTitle,
        reviewCount,
        issueCount,
        chartLabels: weekLabels,
        reviewsData,
        issuesData,
        timeFrameLabel,
        stepBreakdown,
      });
    });
    this.flowAnalytics.sort((a, b) =>
      (a.flowTitle || "").localeCompare(b.flowTitle || "")
    );
    this.updateFilteredFlowAnalytics();
  }

  /** Updates cached filtered list and resets "show more" to initial limit. */
  private updateFilteredFlowAnalytics(): void {
    if (this.flowFilter !== "All") {
      this.filteredFlowAnalytics = this.flowAnalytics.filter((f) => f.flowRowKey === this.flowFilter);
    } else if (!this.searchTerm.trim()) {
      this.filteredFlowAnalytics = this.flowAnalytics;
    } else {
      const visibleFlowIds = new Set(
        this.filteredReviews.map((r) => r.flowRowKey).filter(Boolean)
      );
      this.filteredFlowAnalytics = this.flowAnalytics.filter((f) => visibleFlowIds.has(f.flowRowKey));
    }
    this.analyticsDisplayCount = this.analyticsDisplayLimit;
  }

  getFilteredFlowAnalytics(): FlowAnalyticsItem[] {
    return this.filteredFlowAnalytics;
  }

  /** Slice for template: only render this many chart cards to keep DOM/Chart.js instances low. */
  getDisplayedFlowAnalytics(): FlowAnalyticsItem[] {
    return this.filteredFlowAnalytics.slice(0, this.analyticsDisplayCount);
  }

  getHasMoreAnalyticsToShow(): boolean {
    return this.analyticsDisplayCount < this.filteredFlowAnalytics.length;
  }

  showMoreAnalytics(): void {
    this.analyticsDisplayCount = Math.min(
      this.analyticsDisplayCount + this.analyticsDisplayLimit,
      this.filteredFlowAnalytics.length
    );
  }

  trackByFlowRowKey(_index: number, item: FlowAnalyticsItem): string {
    return item.flowRowKey;
  }

  /** Total number of reviews (for top summary card). Uses filtered list when a flow filter is applied. */
  getTotalReviewsCount(): number {
    return this.filteredReviews?.length ?? 0;
  }

  /** Total number of issues reported (for top summary card). Uses filtered list when a flow filter is applied. */
  getTotalIssuesCount(): number {
    if (!this.filteredReviews?.length) return 0;
    return this.filteredReviews.reduce(
      (sum, r) => sum + (r.issues?.length || 0),
      0
    );
  }

  /** Number of distinct flows in current view (for top summary card). Uses filtered list when a flow filter is applied. */
  getFlowsWithReviewsCount(): number {
    if (!this.filteredReviews?.length) return 0;
    const flowIds = new Set(this.filteredReviews.map((r) => r.flowRowKey).filter(Boolean));
    return flowIds.size;
  }

  respondToReview(review: FlowReview): void {
    // Get response text from either the map (card view) or the single variable (modal view)
    const response = this.reviewResponseText.get(review.rowKey) || this.responseText;
    
    if (!response || !response.trim()) {
      this.toastr.warning("Please enter a response");
      return;
    }

    this.flowServiceService.respondToReview(review.rowKey, response).subscribe(
      (responseData) => {
        this.toastr.success("Response submitted successfully");
        this.reviewResponseText.delete(review.rowKey);
        this.responseText = "";
        this.selectedReview = null;
        this.loadReviews();
      },
      (error) => {
        this.toastr.error("Error submitting response");
        console.error(error);
      }
    );
  }

  getIssueResponse(issueRowKey: string): string {
    return this.issueResponseText.get(issueRowKey) || '';
  }

  setIssueResponse(issueRowKey: string, value: string): void {
    this.issueResponseText.set(issueRowKey, value);
  }

  getReviewResponse(reviewRowKey: string): string {
    return this.reviewResponseText.get(reviewRowKey) || '';
  }

  setReviewResponse(reviewRowKey: string, value: string): void {
    this.reviewResponseText.set(reviewRowKey, value);
  }

  respondToIssue(issue: FlowReviewIssue, review: FlowReview): void {
    const response = this.issueResponseText.get(issue.rowKey);
    if (!response || !response.trim()) {
      this.toastr.warning("Please enter a response");
      return;
    }

    this.flowServiceService.respondToIssue(issue.rowKey, response).subscribe(
      (responseData) => {
        this.toastr.success("Response submitted successfully");
        this.issueResponseText.delete(issue.rowKey);
        this.loadReviews();
      },
      (error) => {
        this.toastr.error("Error submitting response");
        console.error(error);
      }
    );
  }

  moderateReview(review: FlowReview, status: 'Resolved' | 'TakenDown'): void {
    if (!review || !review.rowKey) {
      return;
    }

    this.flowServiceService.moderateReview(review.rowKey, status).subscribe(
      (updated: any) => {
        this.toastr.success(
          status === 'TakenDown'
            ? 'Review taken down and will no longer appear on the flow.'
            : 'Review marked as resolved.'
        );
        // Reload list so dashboard reflects latest moderation state
        this.loadReviews();
      },
      (error) => {
        console.error('Error moderating review', error);
        this.toastr.error('Failed to update review status.');
      }
    );
  }

  moderateIssue(issue: FlowReviewIssue, review: FlowReview, status: 'Resolved' | 'TakenDown'): void {
    if (!issue || !issue.rowKey) {
      return;
    }

    this.flowServiceService.moderateIssue(issue.rowKey, status).subscribe(
      (updated: any) => {
        this.toastr.success(
          status === 'TakenDown'
            ? 'Issue taken down and will no longer appear on the flow.'
            : 'Issue marked as resolved.'
        );
        // Reload to simplify keeping nested arrays in sync
        this.loadReviews();
      },
      (error) => {
        console.error('Error moderating issue', error);
        this.toastr.error('Failed to update issue status.');
      }
    );
  }

  viewFlow(flowRowKey: string): void {
    if (!flowRowKey) {
      return;
    }

    // Try to find the flow title from analytics first, then from reviews
    const titleFromAnalytics =
      this.flowAnalytics.find((f) => f.flowRowKey === flowRowKey)?.flowTitle || "";
    const titleFromReviews =
      this.filteredReviews.find((r) => r.flowRowKey === flowRowKey)?.flowTitle || "";

    const flowTitle = titleFromAnalytics || titleFromReviews;

    if (flowTitle) {
      const slug = this.generateUrlSlug(flowTitle);
      this.router.navigate([`/guide/${slug}/${flowRowKey}`]);
    } else {
      // Fallback: navigate with just the id if title is unavailable
      this.router.navigate([`/guide/${flowRowKey}`]);
    }
  }

  private generateUrlSlug(html: string): string {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove accents
      .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special characters except spaces and dashes
      .trim() // Trim spaces from start/end
      .replace(/\s+/g, "-") // Replace spaces with single dash
      .replace(/-+/g, "-") // Remove duplicate dashes
      .toLowerCase(); // Convert to lowercase
  }

  formatDate(dateString: string): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'table' ? 'card' : 'table';
  }

  toggleReviewSelection(reviewRowKey: string): void {
    if (this.selectedReviews.has(reviewRowKey)) {
      this.selectedReviews.delete(reviewRowKey);
    } else {
      this.selectedReviews.add(reviewRowKey);
    }
  }

  isReviewSelected(reviewRowKey: string): boolean {
    return this.selectedReviews.has(reviewRowKey);
  }

  toggleAllReviews(): void {
    if (this.selectedReviews.size === this.filteredReviews.length) {
      this.selectedReviews.clear();
    } else {
      this.filteredReviews.forEach(review => {
        this.selectedReviews.add(review.rowKey);
      });
    }
  }

  areAllReviewsSelected(): boolean {
    return this.filteredReviews.length > 0 && this.selectedReviews.size === this.filteredReviews.length;
  }

  getSelectedCount(): number {
    return this.selectedReviews.size;
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`;
  }

  getInitials(name: string): string {
    if (!name) return "?";
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return '#999';
    const colors = ['#5ea1d8', '#e83e8c', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#f44336'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  getStarRating(rating: number): { filled: number; half: boolean } {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    return { filled: fullStars, half: hasHalf };
  }

  openReviewDetails(review: FlowReview): void {
    this.selectedReview = review;
    this.responseText = ""; // Clear previous response text
  }

  closeReviewDetails(): void {
    this.selectedReview = null;
    this.responseText = ""; // Clear response text when closing
    this.internalNotes = ""; // Clear internal notes when closing
  }

  getGalleryMedia(): any[] {
    if (!this.selectedReview || !this.selectedReview.issues) return [];
    return this.selectedReview.issues
      .filter(issue => issue.mediaUrl)
      .map(issue => ({
        url: issue.mediaUrl,
        thumbnail: issue.mediaThumbnailUrl,
        isVideo: issue.isVideo
      }));
  }

  flagReview(review: FlowReview): void {
    // TODO: Implement flag review functionality
    this.toastr.info('Flag review functionality coming soon');
  }
}


